import ReactECharts from 'echarts-for-react';
import type { StyleConfig } from '../../../../types/bi';
import { useEChartsTheme } from '../../hooks/useEChartsTheme';
import { buildChartClickHandler } from '../../utils/drilldown';
import type { DrilldownEvent } from '../../utils/drilldown';

interface WaterfallChartProps {
  data: { columns: string[]; rows: Record<string, unknown>[] };
  dimensions: string[];
  measures: { alias: string }[];
  style?: StyleConfig;
  onDrillDown?: (event: DrilldownEvent) => void;
}

export default function WaterfallChart({
  data,
  dimensions,
  measures,
  style,
  onDrillDown,
}: WaterfallChartProps) {
  const theme = useEChartsTheme();

  const dimKey = dimensions[0] || data.columns[0];
  const measureKey = measures[0]?.alias || data.columns[data.columns.length - 1];

  const categories: string[] = [];
  const values: number[] = [];
  for (const row of data.rows) {
    categories.push(String(row[dimKey] ?? ''));
    values.push(Number(row[measureKey]) || 0);
  }

  // Build waterfall segments: transparent base + positive (green) / negative (red) visible bar
  const baseData: (number | { value: number; itemStyle: { color: string } })[] = [];
  const positiveData: (number | { value: number; itemStyle: { color: string } })[] = [];
  const negativeData: (number | { value: number; itemStyle: { color: string } })[] = [];
  let runningTotal = 0;

  const posColour = style?.colours?.[0] || '#4caf50';
  const negColour = style?.colours?.[1] || '#f44336';
  const totalColour = style?.colours?.[2] || 'var(--primary-500, #2196f3)';

  // Add a total column at the end
  const allCategories = [...categories, 'Total'];

  for (let i = 0; i < values.length; i++) {
    const val = values[i];
    if (val >= 0) {
      baseData.push(runningTotal);
      positiveData.push(val);
      negativeData.push(0);
    } else {
      baseData.push(runningTotal + val);
      positiveData.push(0);
      negativeData.push(Math.abs(val));
    }
    runningTotal += val;
  }

  // Total bar — rendered as positive from 0
  baseData.push(0);
  positiveData.push({ value: runningTotal, itemStyle: { color: totalColour } });
  negativeData.push(0);

  const option = {
    tooltip: {
      trigger: 'axis' as const,
      axisPointer: { type: 'shadow' as const },
      formatter: (params: { dataIndex: number }[]) => {
        const idx = params[0].dataIndex;
        if (idx === values.length) {
          return `Total: ${runningTotal}`;
        }
        const val = values[idx];
        const running = values.slice(0, idx + 1).reduce((a, b) => a + b, 0);
        return `${categories[idx]}<br/>Change: ${val >= 0 ? '+' : ''}${val}<br/>Running total: ${running}`;
      },
    },
    grid: { left: '8%', right: '4%', bottom: '8%', top: '8%', containLabel: true },
    xAxis: {
      type: 'category' as const,
      data: allCategories,
      name: style?.axis_labels?.x,
      axisLabel: {
        rotate: allCategories.length > 8 ? 30 : 0,
      },
    },
    yAxis: { type: 'value' as const },
    series: [
      {
        name: 'Base',
        type: 'bar' as const,
        stack: 'waterfall',
        itemStyle: { color: 'transparent' },
        emphasis: { itemStyle: { color: 'transparent' } },
        data: baseData,
      },
      {
        name: 'Increase',
        type: 'bar' as const,
        stack: 'waterfall',
        itemStyle: { color: posColour },
        data: positiveData,
      },
      {
        name: 'Decrease',
        type: 'bar' as const,
        stack: 'waterfall',
        itemStyle: { color: negColour },
        data: negativeData,
      },
    ],
  };

  const events = buildChartClickHandler(dimensions, onDrillDown);

  return (
    <ReactECharts
      option={option}
      theme={theme}
      style={{ height: '100%', width: '100%' }}
      onEvents={events}
    />
  );
}
